"use client";

import { ArrowUpDown } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  PRODUCT_GRID_SORT_OPTIONS,
  sortCriteriaFromSelectValue,
  sortCriteriaToSelectValue,
} from "@/features/products/shared/productGridSortSelect";
import type { ISortCriteria } from "@/lib/shared/types/pagination";
import {
  cn,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/ui";

interface SortProps {
  sortCriteria: ISortCriteria;
  onSortCriteriaChange: (sortCriteria: ISortCriteria) => void;
}

export const Sort = (props: SortProps) => {
  const { onSortCriteriaChange, sortCriteria } = props;
  const t = useTranslations("filter_sort");
  const selectedValue = sortCriteriaToSelectValue(sortCriteria);

  return (
    <Select
      value={selectedValue}
      onValueChange={(value) => {
        const next = sortCriteriaFromSelectValue(value);
        if (next) onSortCriteriaChange(next);
      }}
    >
      <SelectTrigger
        size="default"
        aria-label={t("sort_aria")}
        data-testid="product-sort-trigger"
        className={cn(
          "h-auto min-h-11 w-full gap-3 rounded-xl border-border/70 bg-muted/25 px-4 py-2.5 shadow-sm",
          "hover:border-foreground/30 hover:bg-muted/40",
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40",
          "data-[size=default]:h-auto",
          "sm:min-h-10 sm:max-w-[18rem] sm:rounded-lg sm:px-3.5 sm:py-2",
          "[&>svg]:size-4 [&>svg]:opacity-60",
        )}
      >
        <ArrowUpDown
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left sm:flex-row sm:items-center sm:gap-2">
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground sm:text-xs sm:font-normal sm:normal-case sm:tracking-normal">
            {t("sort_by")}
          </span>
          <SelectValue className="w-full truncate text-sm font-medium leading-snug sm:text-[13px]" />
        </span>
      </SelectTrigger>
      <SelectContent
        align="end"
        position="popper"
        sideOffset={8}
        className={cn(
          "z-50 max-h-[min(16rem,var(--radix-select-content-available-height))] rounded-xl border-border/80 p-1.5 shadow-lg",
          "w-[var(--radix-select-trigger-width)] min-w-[min(100vw-2rem,var(--radix-select-trigger-width))]",
          "sm:rounded-lg sm:p-1",
        )}
      >
        <SelectGroup>
          <SelectLabel className="px-2.5 py-2 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground sm:py-1.5 sm:text-[11px] sm:tracking-wide">
            {t("sort_by")}
          </SelectLabel>
          {PRODUCT_GRID_SORT_OPTIONS.map((option) => {
            const value = sortCriteriaToSelectValue(option);
            return (
              <SelectItem
                key={value}
                value={value}
                className={cn(
                  "cursor-pointer rounded-lg py-2.5 pl-3 pr-9 text-sm leading-snug",
                  "min-h-11 sm:min-h-0 sm:rounded-md sm:py-2 sm:pl-2.5 sm:pr-8 sm:text-[13px]",
                  "focus:bg-accent/80 data-[state=checked]:font-medium",
                )}
              >
                {t(`${option.criteria}_${option.order}`)}
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
